import * as React from "react"

import { cn } from "@/lib/utils"

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: 'none' | 'sm' | 'md' | 'lg' | string;
}

const paddingClasses: Record<string, string> = {
  none: '',
  sm: 'p-3.5 md:p-4',
  md: 'p-5 md:p-6',
  lg: 'p-6 md:p-8',
};

const PADDING_REGEX = /(?:^|\s)p(?:[xytbrl])?-(?:\d+|\[.+?\])/;

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, padding, children, ...props }, ref) => {
    let paddingClass = '';

    if (padding !== undefined) {
      paddingClass = paddingClasses[padding] ?? padding;
    } else {
      const hasPaddingInClass = className && PADDING_REGEX.test(className);
      if (!hasPaddingInClass) {
        const hasComposedChildren = React.Children.toArray(children).some((child) => {
          if (!React.isValidElement(child)) return false;
          const type = child.type as { displayName?: string; name?: string };
          const name = type?.displayName || type?.name;
          return name === 'CardHeader' || name === 'CardContent' || name === 'CardFooter';
        });

        if (!hasComposedChildren) {
          paddingClass = 'p-5 md:p-6';
        }
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-xl border bg-card text-card-foreground shadow-sm transition-all",
          paddingClass,
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
